import React, { useState } from 'react';
import { auth, db } from '../../../services/firebase';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import emailjs from 'emailjs-com';
import Input from '../../common/Input';
import FileUpload from '../../common/FileUpload';
import Button from '../../common/Button';

const PersonalInfoStep = ({ data, updateData, nextStep }) => {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  
  const [shopName, setShopName] = useState(data.shopName || '');
  const [contactPersonName, setContactPersonName] = useState(data.contactPersonName || '');
  const [phone, setPhone] = useState(data.phone || '');
  const [email, setEmail] = useState(data.email || '');
  const [password, setPassword] = useState(data.password || '');
  const [confirmPassword, setConfirmPassword] = useState(data.confirmPassword || '');
  const [businessType, setBusinessType] = useState(data.businessType || 'with-orders');
  const [logo, setLogo] = useState(data.logo || null);
  const [logoPreview, setLogoPreview] = useState(data.logoPreview || null);

  // Regex patterns for validation
  const phoneRegex = /^\d{10}$/;
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;

  const validateForm = () => {
    const newErrors = {};
    
    if (!shopName.trim()) {
      newErrors.shopName = 'Shop name is required';
    }
    
    if (!contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required';
    }
    
    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(phone.trim())) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRegex.test(password)) {
      newErrors.password = 'Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number';
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!emailVerified) {
      newErrors.email = 'Please verify your email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoChange = (file) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, logo: 'Please upload an image file' }));
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, logo: 'Image size must be less than 5MB' }));
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setLogo(file);
      setErrors((prev) => ({ ...prev, logo: '' }));
    } else {
      setLogo(null);
      setLogoPreview(null);
      setErrors((prev) => ({ ...prev, logo: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhone(value);
    }
  };

  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendEmailOTP = async (email, otp) => {
    try {
      const serviceID = 'service_uxp1jfg';
      const templateID = 'template_vggjncg';
      const userID = 'UxoC3_dFCRqDjgnJu';
      
      const templateParams = {
        email: email,
        passcode: otp,
        to_email: email,
        otp: otp,
        code: otp,
      };

      const response = await emailjs.send(serviceID, templateID, templateParams, userID);
      
      if (response.status === 200) {
        console.log('Email sent successfully:', response);
        return true;
      } else {
        console.error('Failed to send email:', response);
        return false;
      }
      
    } catch (error) {
      console.error('Error sending email:', error);
      return false;
    }
  };

  const handleEmailVerification = async () => {
    if (!email.trim()) {
      setErrors({ email: 'Email is required' });
      return;
    }
    
    if (!emailRegex.test(email.trim())) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const otpCode = generateOTP();
      const expiresAt = new Date(Date.now() + 5 * 60 * 1000);
      
      await addDoc(collection(db, 'email_verifications'), {
        email: email.trim().toLowerCase(),
        otp: otpCode,
        createdAt: new Date(),
        expiresAt: expiresAt,
        verified: false
      });

      const emailSent = await sendEmailOTP(email, otpCode);
      
      if (emailSent) {
        setVerificationSent(true);
        setShowOtpModal(true);
        setLoading(false);
      } else {
        throw new Error('Failed to send email');
      }
      
    } catch (error) {
      setLoading(false);
      console.error('Error sending verification:', error);
      setErrors({ general: 'Failed to send verification code. Please try again.' });
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setErrors({ otp: 'Please enter the verification code' });
      return;
    }

    if (otp.length !== 6) {
      setErrors({ otp: 'Verification code must be 6 digits' });
      return;
    }

    setVerifyingOtp(true);
    setErrors({});

    try {
      const q = query(
        collection(db, 'email_verifications'),
        where('email', '==', email.trim().toLowerCase()),
        where('otp', '==', otp.trim()),
        where('verified', '==', false)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setErrors({ otp: 'Invalid or expired verification code' });
        setVerifyingOtp(false);
        return;
      }

      let isValid = false;
      let docToDelete = null;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const expiresAt = data.expiresAt.toDate();
        
        if (expiresAt > new Date()) {
          isValid = true;
          docToDelete = doc.ref;
        }
      });

      if (isValid) {
        if (docToDelete) {
          await deleteDoc(docToDelete);
        }
        
        setEmailVerified(true);
        setShowOtpModal(false);
        setVerifyingOtp(false);
        setOtp('');
        setErrors({});
      } else {
        setErrors({ otp: 'Verification code has expired' });
        setVerifyingOtp(false);
      }
      
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setErrors({ otp: 'Error verifying code. Please try again.' });
      setVerifyingOtp(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp('');
    setErrors({});
    await handleEmailVerification();
  };

  const handleCloseModal = () => {
    setShowOtpModal(false);
    setOtp('');
    setErrors({});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    updateData({
      shopName,
      contactPersonName,
      phone,
      email,
      password,
      businessType,
      logo,
      logoPreview,
      emailVerified: true,
    });
    nextStep();
  };

  return (
    <div className="mt-8">
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600 text-sm">{errors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Logo Upload Section */}
        <div className="flex justify-center mb-6">
          <div className="w-32 h-32">
            <FileUpload
              accept="image/*"
              onChange={handleLogoChange}
              preview={logoPreview}
              defaultPreview="/assets/images/default-shop.png"
              className="w-32 h-32 rounded-full border-4 border-gray-200 overflow-hidden hover:border-orange-300 transition-colors"
              previewClassName="object-cover object-center rounded-full"
              label="Shop Logo"
              error={errors.logo}
            />
          </div>
        </div>

        <Input
          label="Shop Name"
          id="shopName"
          name="shopName"
          type="text"
          value={shopName}
          onChange={(e) => setShopName(e.target.value)}
          error={errors.shopName}
          placeholder="Your shop name"
          required
        />

        <Input
          label="Contact Person Name"
          id="contactPersonName"
          name="contactPersonName"
          type="text"
          value={contactPersonName}
          onChange={(e) => setContactPersonName(e.target.value)}
          error={errors.contactPersonName}
          placeholder="Your name"
          required
        />

        <div className="space-y-2">
          <Input
            label="Phone Number"
            id="phone"
            name="phone"
            type="tel"
            value={phone}
            onChange={handlePhoneChange}
            error={errors.phone}
            placeholder="1234567890"
            maxLength="10"
            required
          />
          <p className="text-xs text-gray-500">Enter exactly 10 digits</p>
        </div>

        {/* Email with verification */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Email Address *
          </label>
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm ${
                  errors.email ? 'border-red-300' : emailVerified ? 'border-green-300 bg-green-50' : 'border-gray-300'
                }`}
                disabled={emailVerified}
                required
              />
            </div>
            <Button
              type="button"
              variant={emailVerified ? "success" : "secondary"}
              onClick={handleEmailVerification}
              disabled={loading || emailVerified || !email}
              className="px-4 py-2 whitespace-nowrap"
            >
              {loading ? 'Sending...' : emailVerified ? '✓ Verified' : 'Verify Email'}
            </Button>
          </div>
          {errors.email && (
            <p className="text-red-600 text-sm">{errors.email}</p>
          )}
          {emailVerified && (
            <p className="text-green-600 text-sm flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Email verified successfully
            </p>
          )}
        </div>

        {/* Business Type Selection */}
        <div className="space-y-2">
          <label htmlFor="businessType" className="block text-sm font-medium text-gray-700">
            Business Type *
          </label>
          <select
            id="businessType"
            name="businessType"
            value={businessType}
            onChange={(e) => setBusinessType(e.target.value)}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
          >
            <option value="with-orders">With Orders</option>
            <option value="without-orders">Without Orders</option>
          </select>
        </div>

        <div className="space-y-2">
          <Input
            label="Password"
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            placeholder="Minimum 8 characters"
            required
          />
          <p className="text-xs text-gray-500">
            Must contain at least 8 characters with 1 uppercase, 1 lowercase, and 1 number
          </p>
        </div>

        <Input
          label="Confirm Password"
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={errors.confirmPassword}
          placeholder="Re-enter password"
          required
        />

        <div className="flex justify-end mt-6">
          <Button 
            type="submit" 
            variant="primary"
            disabled={!emailVerified}
            className={!emailVerified ? 'opacity-50 cursor-not-allowed' : ''}
          >
            Continue to Next Step
          </Button>
        </div>
      </form>

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Enter Verification Code
              </h3>
              <p className="text-gray-600 mb-4">
                We've sent a 6-digit code to <strong>{email}</strong>
              </p>
            </div>

            <div className="space-y-4">
              <Input
                label="6-digit verification code"
                id="otp"
                name="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                error={errors.otp}
                placeholder="123456"
                maxLength="6"
                className="text-center text-lg font-mono"
                required
              />

              {verifyingOtp && (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                  <span className="ml-2 text-sm text-gray-600">Verifying...</span>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleVerifyOTP}
                  disabled={verifyingOtp || !otp || otp.length !== 6}
                  className="w-full"
                >
                  {verifyingOtp ? 'Verifying...' : 'Verify Code'}
                </Button>
                
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleResendOTP}
                  className="w-full"
                >
                  Resend Code
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoStep;